/**
 * Department role profiles (owner command of 2026-10-05).
 *
 * A department is a profile, not a computer and not a container: pre-written role
 * instructions, the tools it may use, and notes it keeps between jobs. A job names one
 * department; whichever free Worker takes the job loads that profile when its turn starts, so
 * the same Worker can be a Coder on one job and Content on the next. Nothing of one profile
 * stays with the Worker: every piece of it is read again from the job.
 *
 * A profile focuses a job. It is never a permission: its instructions sit below the owner
 * contract, the task and the repository's own rules in the shared instruction bundle; its
 * tool list can only take tools away from what the runtime was already launched with; and
 * its notes are the lowest, advisory layer, screened like every learned heuristic.
 *
 * Pure: no I/O, no clock. This module names no runtime and no model.
 */
import {
  INSTRUCTION_ENTRY_LINE_LIMIT,
  INSTRUCTION_LINE_LIMIT,
  isSecretShapedInstruction,
  requiresOwnerAuthority,
  type InstructionEntry,
} from '../instructions/bundle.js';

/** One department as Brunel names it everywhere: the planner, the database and Command. */
export interface DepartmentIdentity {
  readonly key: string;
  readonly label: string;
  /** What kind of job belongs to it, for whoever picks a department for a job. */
  readonly purpose: string;
}

/** The departments Brunel starts with. `brunel_private.department_keys()` mirrors the keys. */
export const DEPARTMENTS: readonly DepartmentIdentity[] = Object.freeze([
  { key: 'coders', label: 'Coders', purpose: 'software in a repository: code, tests, builds, database changes, repairs, and any job that fits none of the others' },
  { key: 'ux', label: 'UX', purpose: 'how the product looks and behaves for the person using it: screens, layout, controls and their wording, accessibility, interaction' },
  { key: 'content', label: 'Content', purpose: 'words for readers: documentation, guides, help text, release notes, articles' },
  { key: 'marketing', label: 'Marketing', purpose: 'reaching an audience: positioning, campaigns, announcements, landing pages, search and social copy' },
  { key: 'sales', label: 'Sales', purpose: 'winning and keeping customers: prospect research, outreach drafts, proposals, price sheets, follow-ups' },
]);

export const DEPARTMENT_KEYS: readonly string[] = Object.freeze(DEPARTMENTS.map((department) => department.key));

const DEPARTMENT_KEY_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;

/**
 * The department a written value means, or null. A key or a label in any letter case is
 * read ("ux", "UX", "Coders"); anything else is not a department, and is never guessed into one.
 */
export function departmentKeyFrom(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const said = value.trim().toLowerCase();
  if (said === '') return null;
  const found = DEPARTMENTS.find((department) => department.key === said || department.label.toLowerCase() === said);
  return found?.key ?? null;
}

/** The name Command shows for a department key. Null for no department or one it does not know. */
export function departmentLabel(key: string | null | undefined): string | null {
  if (key === null || key === undefined) return null;
  return DEPARTMENTS.find((department) => department.key === key)?.label ?? null;
}

/**
 * The tools a profile may name. Each is a kind of tool, not one runtime's name for it; the
 * launch catalog says what each means for a runtime and how that runtime is told.
 */
export const DEPARTMENT_TOOLS = ['read', 'write', 'shell', 'web', 'connectors'] as const;
export type DepartmentTool = (typeof DEPARTMENT_TOOLS)[number];

export const DEPARTMENT_TOOL_LABEL: Readonly<Record<DepartmentTool, string>> = Object.freeze({
  read: 'read and search files',
  write: 'create and edit files',
  shell: 'run commands',
  web: 'read the web',
  connectors: 'use connected apps',
});

/** One department's profile: its pre-written instructions and the tools it may use. Data, not prompt code. */
export interface DepartmentProfile {
  readonly key: string;
  readonly label: string;
  readonly version: string;
  readonly instructions: readonly string[];
  readonly tools: readonly DepartmentTool[];
}

/**
 * The one line every department carries, in the catalog and in the text composed for a run.
 * A Worker stays inside the checkout this run was given.
 */
export const DEPARTMENT_CHECKOUT_BOUNDARY =
  'Work only inside the run\'s own checkout; never create worktrees, clones or repository copies elsewhere on the computer; before finishing, remove scratch files or folders you created outside that checkout.';

/** How many of a profile's own lines fit beside the lines every profile carries. */
export const DEPARTMENT_INSTRUCTION_LIMIT = INSTRUCTION_ENTRY_LINE_LIMIT - 8;

function isBoundedLine(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '' && value.length <= INSTRUCTION_LINE_LIMIT && !/[\r\n\0]/.test(value);
}

/**
 * Parse a profile catalog ({ departments: [...] }). Only a department Brunel names is read, and
 * an invalid or repeated entry is dropped whole, so a bad profile shows as a job with no
 * profile loaded rather than as a partly trusted one.
 */
export function parseDepartmentProfiles(value: unknown): DepartmentProfile[] {
  if (value === null || typeof value !== 'object' || !Array.isArray((value as { departments?: unknown }).departments)) return [];
  const out: DepartmentProfile[] = [];
  const seen = new Set<string>();
  for (const raw of (value as { departments: unknown[] }).departments) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const item = raw as Record<string, unknown>;
    const key = item['key'];
    if (typeof key !== 'string' || !DEPARTMENT_KEY_PATTERN.test(key) || seen.has(key)) continue;
    const identity = DEPARTMENTS.find((department) => department.key === key);
    if (identity === undefined) continue;
    if (typeof item['version'] !== 'string' || !/^[A-Za-z0-9._-]{1,40}$/.test(item['version'])) continue;
    const instructions = item['instructions'];
    if (!Array.isArray(instructions) || instructions.length === 0 || instructions.length > DEPARTMENT_INSTRUCTION_LIMIT
      || !instructions.every(isBoundedLine)) continue;
    const tools = item['tools'];
    if (!Array.isArray(tools) || tools.length === 0
      || !tools.every((tool) => (DEPARTMENT_TOOLS as readonly unknown[]).includes(tool))) continue;
    seen.add(key);
    out.push({
      key,
      label: identity.label,
      version: item['version'],
      instructions: instructions as string[],
      // In the vocabulary's own order, each once.
      tools: DEPARTMENT_TOOLS.filter((tool) => tools.includes(tool)),
    });
  }
  return out;
}

export function departmentProfile(profiles: readonly DepartmentProfile[], key: string | null | undefined): DepartmentProfile | null {
  if (key === null || key === undefined) return null;
  return profiles.find((profile) => profile.key === key) ?? null;
}

/** The tools of the vocabulary a profile leaves out. */
export function departmentToolsWithheld(profile: DepartmentProfile): readonly DepartmentTool[] {
  return DEPARTMENT_TOOLS.filter((tool) => !profile.tools.includes(tool));
}

/** One note a department kept from an earlier job, as the run brief carries it. */
export interface DepartmentNote {
  readonly id: string;
  readonly body: string;
}

/** The department a job names, and that department's notes, exactly as the run brief gave them. */
export interface DepartmentBrief {
  readonly key: string;
  readonly notes: readonly DepartmentNote[];
}

/** The most notes one job is shown, and the most one job may leave. */
export const DEPARTMENT_NOTES_SHOWN = 12;
export const DEPARTMENT_NOTES_PER_JOB = 3;
export const DEPARTMENT_NOTE_MAX = 400;
/** What a Worker starts a line of its final reply with to leave its department a note. */
export const DEPARTMENT_NOTE_PREFIX = 'DEPARTMENT NOTE:';

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The run brief's department, or null when the job names none. A key that is not a department
 * is no department; a malformed note is dropped rather than costing the job its profile.
 */
export function parseDepartmentBrief(value: unknown): DepartmentBrief | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const key = raw['key'];
  if (typeof key !== 'string' || !DEPARTMENT_KEYS.includes(key)) return null;
  const notes: DepartmentNote[] = [];
  const seen = new Set<string>();
  for (const entry of Array.isArray(raw['notes']) ? raw['notes'] : []) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const id = (entry as Record<string, unknown>)['id'];
    const body = (entry as Record<string, unknown>)['body'];
    if (typeof id !== 'string' || !UUID_PATTERN.test(id) || seen.has(id)) continue;
    if (typeof body !== 'string' || body.trim() === '' || body.length > DEPARTMENT_NOTE_MAX || /[\r\n\0]/.test(body)) continue;
    seen.add(id);
    notes.push({ id, body: body.trim() });
    if (notes.length >= DEPARTMENT_NOTES_SHOWN) break;
  }
  return { key, notes };
}

function toolSentence(profile: DepartmentProfile): string {
  const may = profile.tools.map((tool) => DEPARTMENT_TOOL_LABEL[tool]).join('; ');
  const withheld = departmentToolsWithheld(profile).map((tool) => DEPARTMENT_TOOL_LABEL[tool]);
  return `Tools this department may use: ${may}.`
    + (withheld.length === 0 ? '' : ` Not for this department: ${withheld.join('; ')}. Do not use them for this job, and do not work around their absence.`);
}

/**
 * A job's department as instruction-bundle entries.
 *
 * The profile is one `role_playbook` entry, so it reads below the owner contract and the
 * current task and can override neither. Each note is its own `learned_heuristic` entry, the
 * lowest layer: the shared resolver drops any that touches a domain needing owner authority
 * or looks like a credential, and one dropped note never costs the others.
 */
export function departmentInstructionEntries(profile: DepartmentProfile, notes: readonly DepartmentNote[]): InstructionEntry[] {
  return [
    {
      layer: 'role_playbook',
      key: `department:${profile.key}`,
      lines: [
        `Department for this job: ${profile.label}. You work as this department for this job only. Another job may put you in a different department; nothing from another department's profile, tools or notes applies to this one.`,
        // The checkout line is stated once, whether or not the catalog already carries it.
        ...profile.instructions.filter((line) => line !== DEPARTMENT_CHECKOUT_BOUNDARY),
        DEPARTMENT_CHECKOUT_BOUNDARY,
        toolSentence(profile),
        'A department profile focuses the job. It never outranks the owner contract, the current task or the repository\'s own rules above, and it grants no permission.',
        `${profile.label} keeps notes between jobs in this project; any it holds are listed further down, each marked "${profile.label} note". They are advisory.`,
        `To leave a note for the next ${profile.label} job, add a line to your final reply that starts with "${DEPARTMENT_NOTE_PREFIX}" followed by one sentence (at most ${DEPARTMENT_NOTES_PER_JOB} such lines, each before the RESULT line). Note only a lasting fact about how this department works in this project. A note that mentions merging, releasing, deploying, production, spending, credentials, permissions, approval, security, or an AI model, runtime, provider or route is not kept, so word it without them.`,
      ],
      provenance: { source: 'department_profiles', ref: profile.key, version: profile.version },
    },
    ...notes.slice(0, DEPARTMENT_NOTES_SHOWN).map((note): InstructionEntry => ({
      layer: 'learned_heuristic',
      key: `department_note:${note.id}`,
      lines: [`${profile.label} note: ${note.body}`],
      provenance: { source: 'brunel.department_notes', ref: note.id, version: null },
    })),
  ];
}

/**
 * The notes a Worker left in its final reply: each line that starts with the note prefix,
 * in order, at most DEPARTMENT_NOTES_PER_JOB. Only the reply's own lines are read, so
 * nothing a tool printed (indented in the run's record) can leave a note.
 */
export function departmentNotesFromReply(reply: string): string[] {
  const notes: string[] = [];
  for (const line of reply.replace(/\r\n?/g, '\n').split('\n')) {
    if (!line.startsWith(DEPARTMENT_NOTE_PREFIX)) continue;
    const body = line.slice(DEPARTMENT_NOTE_PREFIX.length).replace(/\s+/g, ' ').trim();
    if (body === '' || notes.includes(body)) continue;
    notes.push(body);
    if (notes.length >= DEPARTMENT_NOTES_PER_JOB) break;
  }
  return notes;
}

export type DepartmentNoteRefusal = 'too_long' | 'placeholder' | 'secret_shaped' | 'requires_owner_authority';

/**
 * Whether a note may be kept. The same two screens the instruction resolver applies when a
 * note is read back, applied before it is stored, so what is kept is what can be shown.
 */
export function departmentNoteRefusal(body: string, isSecretShaped: (text: string) => boolean = isSecretShapedInstruction): DepartmentNoteRefusal | null {
  if (body.length > DEPARTMENT_NOTE_MAX) return 'too_long';
  // The brief's own example of the line is not a note.
  if (/^<[^>]*>$/.test(body) || /^followed by one sentence\b/i.test(body)) return 'placeholder';
  if (isSecretShaped(body)) return 'secret_shaped';
  if (requiresOwnerAuthority(body)) return 'requires_owner_authority';
  return null;
}

/** The planner's one contract line about departments: which exist, and that the owner's naming wins. */
export function plannerDepartmentLine(): string {
  return '"department" is which department profile the Worker loads for the task. Choose exactly one for every task from: '
    + DEPARTMENTS.map((department) => `"${department.key}" (${department.purpose})`).join('; ')
    + '. When the owner\'s command names a department, use that one. A department is a profile any free Worker loads; it never chooses the Worker.';
}
